/* eslint-disable no-nested-ternary */
/* eslint react-hooks/exhaustive-deps: "error" */
import React, { Fragment } from 'react';
import styled from 'styled-components';

import { UseCountDownState } from 'crehana-react-hooks/useCountdown';

import { LazyImage, PrimaryButton } from '@crehana/ui';
import { LockCircle } from '@crehana/icons/fill';

import useYouTubePlayer, { getYTembedId } from 'Shared/Hooks/useYoutubePlayer';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { LiveNode } from 'Jsx/CrehanaLive/graphql/types/LiveNode';
import VideoInfo from 'Jsx/CrehanaLive/components/VideoInfo';
import { TABS_WIDTH_MD, TABS_WIDTH_LG } from 'Jsx/CrehanaLive/constants';

const YoutubeVideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  iframe {
    height: 100%;
    width: 100%;
  }

  .fb_iframe_widget_fluid_desktop,
  .fb_iframe_widget_fluid_desktop span,
  .fb_iframe_widget_fluid_desktop iframe {
    width: 100% !important;
    background: #fff;
  }
`;

const BackdropImage = styled(LazyImage)`
  &:before {
    content: '';
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.base['main-80']};
  }
`;

const PlayerWrapper = styled.div`
  height: calc((9 / 16) * 100vw);
  ${({ theme }) => theme.breakpoints.up('md')} {
    height: calc((9 / 16) * (100vw - ${TABS_WIDTH_MD}));
    max-height: calc(100vh - 372px);
  }
  ${({ theme }) => theme.breakpoints.up('lg')} {
    height: calc((9 / 16) * (100vw - ${TABS_WIDTH_LG}));
    max-height: calc(100vh - 195px);
  }
`;

interface PlayerProps {
  live: LiveNode;
  liveCountdownToStart: UseCountDownState;
  liveIsActive: boolean;
  isFuture: boolean;
  isInThePast: boolean;
  fullWidth?: boolean;
  enrollToLive?: (liveId: number) => Promise<void>;
  setDidFinishLive?: React.Dispatch<React.SetStateAction<boolean>>;
  isForbiddenPremiumLive?: boolean;
  premiumLandingUrl: string;
}

const Player: React.FC<PlayerProps> = ({
  live,
  liveCountdownToStart,
  liveIsActive,
  isFuture,
  enrollToLive,
  setDidFinishLive,
  isForbiddenPremiumLive,
  premiumLandingUrl,
}) => {
  const { t } = useCrehanaLiveTranslations();

  const {
    rawDays,
    days,
    hours,
    minutes,
    seconds,
    finished: liveHasStarted,
  } = liveCountdownToStart;

  useYouTubePlayer({
    videoId: live.url ? getYTembedId(live.url) : undefined,
    events: {
      onStateChange: ({ data }) => {
        if (data === 0 && setDidFinishLive) {
          setDidFinishLive(true);
        }
      },
    },
  });

  const renderPlayerContent = () => {
    if (isFuture || isForbiddenPremiumLive) {
      return (
        <div
          style={{ backgroundColor: 'rgba(15, 17, 36, 0.7)' }}
          className="relative flex flex-col items-center justify-center w-full h-full"
        >
          <div className="absolute inset-0 z-0">
            <BackdropImage
              className="w-full h-full"
              src={live.coverCard || ''}
              alt={live.title}
              imgClassName="object-cover"
              isDark
            />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isForbiddenPremiumLive ? (
              <Fragment>
                <div className="flex flex-col items-center justify-center text-center text-white z-1 sm:hidden">
                  <LockCircle
                    size={40}
                    ignoreColor
                    className="fill-current mb-8"
                  />
                  <p className="font-subtitle3 mb-16">
                    {t('EXCLUSIVE_CONTENT')}
                    <br />
                    {t('PREMIUM_CREHANA')}
                  </p>
                  <PrimaryButton
                    label={t('GO_PREMIUM')}
                    href={premiumLandingUrl}
                    size="xs"
                    isDark
                  />
                </div>
                <div className="hidden sm:flex flex-col items-center justify-center text-center text-white z-1">
                  <LockCircle
                    size={72}
                    ignoreColor
                    className="fill-current mb-20"
                  />
                  <p className="font-h3 mb-24">
                    {t('EXCLUSIVE_CONTENT')}
                    <br />
                    {t('PREMIUM_CREHANA')}
                  </p>
                  <PrimaryButton
                    label={t('GO_PREMIUM')}
                    href={premiumLandingUrl}
                    isDark
                  />
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <p className="font-body3 text-gray-dark z-1">{t('START_IN')}</p>
                <p className="font-h1 text-white mt-16 z-1">
                  {`${
                    rawDays > 0 ? `${days} : ` : ''
                  }${hours} : ${minutes} : ${seconds}`}
                </p>
              </Fragment>
            )}
          </div>
        </div>
      );
    }
    if (live.url && (liveHasStarted || liveIsActive)) {
      const isVimeo = live.videoProvider === 'vimeo';

      return isVimeo ? (
        <div className="relative w-full h-full">
          <iframe
            title="Vimeo"
            src={live.url}
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </div>
      ) : (
        <YoutubeVideoWrapper>
          <div id="YTPlayer" />
        </YoutubeVideoWrapper>
      );
    }
    return (
      <div className="w-full h-full">
        <BackdropImage
          className="w-full h-full"
          src={live.coverCard || ''}
          alt={live.title}
        />
      </div>
    );
  };

  return (
    <div>
      <PlayerWrapper className="bg-base-dark flex items-center justify-center w-full h-full">
        {renderPlayerContent()}
      </PlayerWrapper>
      <div className="hidden p-16 md:block md:py-16 md:pl-72 md:pr-48">
        <VideoInfo
          {...live}
          isFuture={isFuture}
          liveHasStarted={liveHasStarted}
          liveIsActive={liveIsActive}
          enrollToLive={enrollToLive}
          isForbiddenPremiumLive={isForbiddenPremiumLive}
        />
      </div>
    </div>
  );
};

export default Player;
